import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * "Save to Library" button with inline feedback: spinner while the save is
 * in flight, then a check + "Saved" for a couple of seconds on success.
 * Failures return the button to idle (the save handler is responsible for
 * surfacing the error, e.g. via a toast).
 */
export function SaveToLibraryButton({
  onSave,
  className,
}: {
  /** Return false (or throw) to indicate the save failed. */
  onSave: () => Promise<boolean | void> | boolean | void;
  className?: string;
}) {
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const handleClick = async () => {
    if (state !== 'idle') return;
    setState('saving');
    let ok = true;
    try {
      const result = await onSave();
      ok = result !== false;
    } catch {
      ok = false;
    }
    if (!mounted.current) return;
    if (ok) {
      setState('saved');
      setTimeout(() => { if (mounted.current) setState('idle'); }, 2000);
    } else {
      setState('idle');
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={state !== 'idle'}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 disabled:opacity-80',
        className
      )}
    >
      {state === 'saving' && <Loader2 className="w-3 h-3 animate-spin" />}
      {state === 'saved' && <Check className="w-3 h-3 text-green-500" />}
      {state === 'saved' ? 'Saved' : 'Save to Library'}
    </button>
  );
}
